import { httpClient } from "./client";
import type { CommunityApi, CommunityCreateInput } from "./interfaces";
import type {
  CommentListResponse,
  // GENERATED DTOs (ADR-005). CommunityResponse carries owner_facility_id (S2.1);
  // CommunityUpdate / PostResponse / CommentResponse are the authoritative shapes
  // — the hand-written ../types/community mirror was deleted. The create body uses
  // the CommunityCreateInput contract type (defined alongside CommunityApi).
  CommunityUpdate,
  CommunityResponse,
  CommunityAdmissionInvitationCreate,
  CommunityAdmissionListResponse,
  CommunityChannelCreate,
  CommunityChannelUpdate,
  CommunityChannelResponse,
  CommunityChannelListResponse,
  CommunityGameNightCreate,
  CommunityGameNightUpdate,
  CommunityGameNightRsvpRequest,
  CommunityGameNightResponse,
  CommunityGameNightListResponse,
  CommunityGameNightReminderResponse,
  CommunityNotificationPreferenceUpdate,
  CommunityNotificationPreferenceResponse,
  CommunityPollCreate,
  CommunityPollResponse,
  CommunityPollListResponse,
  CommunityPollVoteRequest,
  CommunityPollConvertRequest,
  CommunityAdmissionResponse,
  PostResponse,
  PostUpdate,
  CommentResponse,
} from "../types/generated";
import type { PostCreate, CommentCreate, UUID } from "../types";

export const communityApi: CommunityApi = {
  listChannels: (communityId: UUID) =>
    httpClient.get<CommunityChannelListResponse>(`/communities/${communityId}/channels`),
  createChannel: (communityId: UUID, data: CommunityChannelCreate) =>
    httpClient.post<CommunityChannelResponse>(`/communities/${communityId}/channels`, data),
  updateChannel: (conversationId: UUID, data: CommunityChannelUpdate) =>
    httpClient.patch<CommunityChannelResponse>(`/communities/channels/${conversationId}`, data),
  archiveChannel: (conversationId: UUID) =>
    httpClient.post<CommunityChannelResponse>(`/communities/channels/${conversationId}/archive`),
  listGameNights: (communityId: UUID, includePast = false) =>
    httpClient.get<CommunityGameNightListResponse>(
      `/communities/${communityId}/game-nights?include_past=${includePast}`,
    ),
  getGameNight: (eventId: UUID) =>
    httpClient.get<CommunityGameNightResponse>(
      `/communities/game-nights/${eventId}`,
    ),
  createGameNight: (communityId: UUID, data: CommunityGameNightCreate) =>
    httpClient.post<CommunityGameNightResponse>(
      `/communities/${communityId}/game-nights`,
      data,
    ),
  updateGameNight: (eventId: UUID, data: CommunityGameNightUpdate) =>
    httpClient.patch<CommunityGameNightResponse>(
      `/communities/game-nights/${eventId}`,
      data,
    ),
  publishGameNight: (eventId: UUID) =>
    httpClient.post<CommunityGameNightResponse>(
      `/communities/game-nights/${eventId}/publish`,
    ),
  cancelGameNight: (eventId: UUID) =>
    httpClient.post<CommunityGameNightResponse>(
      `/communities/game-nights/${eventId}/cancel`,
    ),
  setGameNightRsvp: (eventId: UUID, data: CommunityGameNightRsvpRequest) =>
    httpClient.put<CommunityGameNightResponse>(
      `/communities/game-nights/${eventId}/rsvp`,
      data,
    ),
  sendGameNightReminder: (eventId: UUID) =>
    httpClient.post<CommunityGameNightReminderResponse>(
      `/communities/game-nights/${eventId}/reminders`,
    ),
  getNotificationPreferences: (communityId: UUID) =>
    httpClient.get<CommunityNotificationPreferenceResponse>(
      `/communities/${communityId}/notification-preferences`,
    ),
  updateNotificationPreferences: (
    communityId: UUID,
    data: CommunityNotificationPreferenceUpdate,
  ) =>
    httpClient.put<CommunityNotificationPreferenceResponse>(
      `/communities/${communityId}/notification-preferences`,
      data,
    ),
  listPolls: (communityId: UUID) =>
    httpClient.get<CommunityPollListResponse>(
      `/communities/${communityId}/polls`,
    ),
  createPoll: (communityId: UUID, data: CommunityPollCreate) =>
    httpClient.post<CommunityPollResponse>(
      `/communities/${communityId}/polls`,
      data,
    ),
  votePoll: (pollId: UUID, data: CommunityPollVoteRequest) =>
    httpClient.put<CommunityPollResponse>(
      `/communities/polls/${pollId}/votes`,
      data,
    ),
  convertPollToGameNight: (pollId: UUID, data: CommunityPollConvertRequest) =>
    httpClient.post<CommunityGameNightResponse>(
      `/communities/polls/${pollId}/convert`,
      data,
    ),
  list: () => httpClient.get<CommunityResponse[]>("/communities"),

  create: (data: CommunityCreateInput) =>
    httpClient.post<CommunityResponse>("/communities", data),

  get: (communityId: UUID) =>
    httpClient.get<CommunityResponse>(`/communities/${communityId}`),

  update: (communityId: UUID, data: CommunityUpdate) =>
    httpClient.patch<CommunityResponse>(`/communities/${communityId}`, data),

  // S2.1 — the facility's single active community (its "club"). GET returns the
  // CommunityResponse or null (the route's response_model is `CommunityResponse |
  // None` — a 200 + null body when the facility has no community yet, NOT a 404).
  getFacilityCommunity: (facilityId: UUID) =>
    httpClient.get<CommunityResponse | null>(
      `/communities/facility/${facilityId}`,
    ),

  // POST creates the facility's community (RBAC facility:community — owner+manager).
  // A second active community for the facility → 409 (FacilityCommunityExistsError).
  createFacilityCommunity: (facilityId: UUID, data: CommunityCreateInput) =>
    httpClient.post<CommunityResponse>(
      `/communities/facility/${facilityId}`,
      data,
    ),

  delete: (communityId: UUID) =>
    httpClient.delete<void>(`/communities/${communityId}`),

  join: (communityId: UUID) =>
    httpClient.post<void>(`/communities/${communityId}/join`),

  leave: (communityId: UUID) =>
    httpClient.post<void>(`/communities/${communityId}/leave`),

  createJoinRequest: (communityId: UUID) =>
    httpClient.post<CommunityAdmissionResponse>(
      `/communities/${communityId}/admissions/request`,
    ),

  createInvitation: (
    communityId: UUID,
    data: CommunityAdmissionInvitationCreate,
  ) =>
    httpClient.post<CommunityAdmissionResponse>(
      `/communities/${communityId}/admissions/invitation`,
      data,
    ),

  listAdmissions: (communityId: UUID, params) => {
    const query = new URLSearchParams({
      status: params?.status ?? "pending",
      limit: String(params?.limit ?? 50),
      offset: String(params?.offset ?? 0),
    });
    return httpClient.get<CommunityAdmissionListResponse>(
      `/communities/${communityId}/admissions?${query.toString()}`,
    );
  },

  listMyAdmissions: (params) => {
    const query = new URLSearchParams({
      status: params?.status ?? "pending",
      limit: String(params?.limit ?? 50),
      offset: String(params?.offset ?? 0),
    });
    return httpClient.get<CommunityAdmissionListResponse>(
      `/communities/admissions/mine?${query.toString()}`,
    );
  },

  approveRequest: (admissionId: UUID) =>
    httpClient.post<CommunityAdmissionResponse>(
      `/communities/admissions/${admissionId}/approve`,
    ),

  declineRequest: (admissionId: UUID) =>
    httpClient.post<CommunityAdmissionResponse>(
      `/communities/admissions/${admissionId}/decline-request`,
    ),

  cancelRequest: (admissionId: UUID) =>
    httpClient.post<CommunityAdmissionResponse>(
      `/communities/admissions/${admissionId}/cancel`,
    ),

  acceptInvitation: (admissionId: UUID) =>
    httpClient.post<CommunityAdmissionResponse>(
      `/communities/admissions/${admissionId}/accept`,
    ),

  declineInvitation: (admissionId: UUID) =>
    httpClient.post<CommunityAdmissionResponse>(
      `/communities/admissions/${admissionId}/decline-invitation`,
    ),

  revokeInvitation: (admissionId: UUID) =>
    httpClient.post<CommunityAdmissionResponse>(
      `/communities/admissions/${admissionId}/revoke`,
    ),

  createPost: (communityId: UUID, data: PostCreate) =>
    httpClient.post<PostResponse>(`/communities/${communityId}/posts`, data),

  // S1d — a PERSONAL post (no community). The server forces community_id to null,
  // so we send only the content + media_urls the composer collected.
  createPersonalPost: (data: PostCreate) =>
    httpClient.post<PostResponse>("/communities/posts", {
      content: data.content,
      media_urls: data.media_urls ?? [],
    }),

  getPost: (postId: UUID) =>
    httpClient.get<PostResponse>(`/communities/posts/${postId}`),

  // Author-only partial edit (PATCH). The body carries ONLY the changed fields
  // (exclude_unset semantics — an omitted field is left untouched server-side).
  // Author-only (403), 404 missing, 422 when the merged result would be invalid
  // (empty non-repost / media on a repost / media cap). Sets `edited_at`.
  updatePost: (postId: UUID, data: PostUpdate) =>
    httpClient.patch<PostResponse>(`/communities/posts/${postId}`, data),

  deletePost: (postId: UUID) =>
    httpClient.delete<void>(`/communities/posts/${postId}`),

  createComment: (postId: UUID, data: CommentCreate) =>
    httpClient.post<CommentResponse>(
      `/communities/posts/${postId}/comments`,
      data,
    ),

  listPostComments: (postId: UUID, params) =>
    httpClient.get<CommentListResponse>(
      `/communities/posts/${postId}/comments?limit=${params?.limit ?? 50}&offset=${params?.offset ?? 0}`,
    ),

  deleteComment: (commentId: UUID) =>
    httpClient.delete<void>(`/communities/comments/${commentId}`),

  // #1836 — first-class comment Like (mirrors likePost/unlikePost). Identity is
  // the token user server-side; the FE sends only the comment id (no body). The
  // generic /communities/votes primitive was retired for comments — this is the
  // one comment-engagement seam now.
  likeComment: (commentId: UUID) =>
    httpClient.post<void>(`/communities/comments/${commentId}/like`),

  unlikeComment: (commentId: UUID) =>
    httpClient.post<void>(`/communities/comments/${commentId}/unlike`),

  // S1a — first-class Like (facade over the vote primitive). Identity is the
  // token user server-side; the FE sends only the post id (no body).
  likePost: (postId: UUID) =>
    httpClient.post<void>(`/communities/posts/${postId}/like`),

  unlikePost: (postId: UUID) =>
    httpClient.post<void>(`/communities/posts/${postId}/unlike`),

  // S1b — repost. Identity is the token user server-side; the FE sends only the
  // post id + an optional `quote` (omitted → a pure repost with empty content)
  // and an optional `community_id` — when present the repost lands in THAT
  // community's feed (same membership/authorization as posting there); when
  // absent it is a repost into the caller's OWN friends feed. The body is sent
  // only when it carries at least one field, so a pure repost-to-own-feed posts
  // no body (preserving the original 2-arg contract byte-for-byte).
  createRepost: (postId: UUID, quote?: string, communityId?: string) => {
    const body: { quote?: string; community_id?: string } = {};
    if (quote != null && quote !== "") body.quote = quote;
    if (communityId != null && communityId !== "")
      body.community_id = communityId;
    return httpClient.post<PostResponse>(
      `/communities/posts/${postId}/repost`,
      Object.keys(body).length > 0 ? body : undefined,
    );
  },
};
